// #phone-auth-unify Part C — single web hook for every OTP entry
// point (Signup, AddPhonePrompt, ForgotPassword).
//
// Identical TypeScript interface to apps/mobile/src/hooks/
// useFirebasePhoneAuth.ts. Web differs internally because:
//   - Firebase Web SDK exposes signInWithPhoneNumber + ConfirmationResult
//     directly (no RNFirebase shim); the hook calls them directly.
//   - reCAPTCHA mount + cleanup was scattered across each consumer; the
//     hook now owns that lifecycle. Consumers still render the
//     <div id="recaptcha-container" /> mount point (it must exist BEFORE
//     the verifier is constructed); the hook handles construction +
//     reset on cooldown / verify failure.
//
// Preserves the [firebase-otp] diagnostic log line shape from 6bdf5af
// so adb logcat / DevTools grep behaviour is uniform across platforms.
//
// Closes the mapper-coverage gap in ForgotPassword: every catch now
// routes through firebaseAuthErrors.ts.

import { useCallback, useEffect, useRef, useState } from 'react'
import { signInWithPhoneNumber, type ConfirmationResult } from 'firebase/auth'
import { getFirebaseAuth, getRecaptchaVerifier, resetRecaptcha } from '../config/firebase'
import { mapFirebaseAuthSendOtpError, mapFirebaseAuthVerifyOtpError } from '../utils/firebaseAuthErrors'
import { prepareForSubmit, type CountryCode } from '@jnati/phone'
import { apiClient } from '../api/client'

export type PhoneAuthStatus =
  | 'idle'
  | 'sending'
  | 'awaiting-code'
  | 'verifying'
  | 'verified'
  | 'duplicate'  // #account-link-1.4: pre-SMS check found the phone is taken
  | 'error'

export interface PhoneAuthState {
  status: PhoneAuthStatus
  error: string | null
  cooldownUntil: number | null
  // #otp-cooldown-ui: live remaining ms in the current cooldown,
  // ticked every second by an internal setInterval. null when no
  // cooldown is active. Consumers render via formatCooldown to get
  // "M:SS" in the Send/Resend button area, then re-enable the button
  // automatically when this returns to null.
  cooldownMsRemaining: number | null
  idToken: string | null
}

const INITIAL_STATE: PhoneAuthState = {
  status: 'idle',
  error: null,
  cooldownUntil: null,
  cooldownMsRemaining: null,
  idToken: null,
}

const COOLDOWN_MS = 5 * 60 * 1000

// #forgot-password-semantic-invert: callers tell the hook what
// the precheck SHOULD mean for their flow. signup (default):
// 409 (phone taken) blocks with "already registered". forgot-password:
// 200 (phone free) blocks with "no account exists" — inverted because
// the reset flow REQUIRES the phone to belong to an existing account.
// Both intents fall through to Firebase on unknown precheck results
// (5xx/network/etc) — same conservative behavior as the prior single-
// intent implementation.
export type SendOtpIntent = 'signup' | 'forgot-password'

export interface UseFirebasePhoneAuth {
  state: PhoneAuthState
  sendOtp: (rawPhone: string, options?: { intent?: SendOtpIntent; country?: CountryCode }) => Promise<void>
  verifyOtp: (code: string) => Promise<string>
  reset: () => void
}

export function useFirebasePhoneAuth(): UseFirebasePhoneAuth {
  const [state, setState] = useState<PhoneAuthState>(INITIAL_STATE)
  // ConfirmationResult is one-shot — created on send, consumed on
  // verify. Held in component-instance state via useState so it
  // survives re-renders between the send and verify steps.
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null)
  // #dup-sms (Release C): synchronous re-entrancy guard for sendOtp. The
  // button's disabled={sending} relies on React committing state, which
  // happens AFTER the handler runs — a fast double-submit could slip a
  // second call through and fire two SMS. This ref flips true synchronously
  // at the top of sendOtp and resets in its finally, closing that gap.
  const sendingRef = useRef(false)

  // #otp-cooldown-ui: tick cooldownMsRemaining once per second whenever
  // a cooldown is active. When it hits 0 we drop both cooldownUntil
  // AND cooldownMsRemaining back to null, and lift the 'error' status
  // back to 'idle' so the consumer's button re-enables automatically.
  // Dep array is [state.cooldownUntil] alone so the interval restarts
  // only when the cooldown timestamp itself changes.
  useEffect(() => {
    if (state.cooldownUntil === null) return
    const tick = () => {
      const remaining = state.cooldownUntil! - Date.now()
      if (remaining <= 0) {
        setState((s) => ({
          ...s,
          status: s.status === 'error' ? 'idle' : s.status,
          cooldownUntil: null,
          cooldownMsRemaining: null,
        }))
      } else {
        setState((s) => ({ ...s, cooldownMsRemaining: remaining }))
      }
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [state.cooldownUntil])

  const sendOtp = useCallback(async (
    rawPhone: string,
    options?: { intent?: SendOtpIntent; country?: CountryCode },
  ): Promise<void> => {
    // #dup-sms: airtight re-entrancy guard — ignore a second call while one
    // is in flight (body wrapped in try/finally that resets the ref).
    if (sendingRef.current) return
    sendingRef.current = true
    try {
    const intent: SendOtpIntent = options?.intent ?? 'signup'
    const country: CountryCode = options?.country ?? 'IN'

    // #intl-auth-1c: one shared decision (country + typed number). India
    // (the default) takes the exact pre-1c path — normalizePhone + toE164
    // — so an Indian send is byte-identical to before. Malformed → a
    // validation message. Valid non-India → a clean "coming soon" (the
    // selector works, but the country isn't functional until the Phase 2
    // migration); we never reach the backend for it.
    const prep = prepareForSubmit(rawPhone, country)
    if (!prep.ok) {
      setState({ ...INITIAL_STATE, status: 'error', error: prep.message })
      throw new Error(prep.message)
    }
    const e164 = prep.e164

    // #account-link-1.4 + #forgot-password-semantic-invert: pre-SMS
    // duplicate check, interpreted per the caller's intent.
    //   signup           — 409 blocks ("phone already registered")
    //   forgot-password  — 200 blocks ("no account exists")
    // Endpoint is public (post #auth-phone-availability-public); rate
    // limited at 30 req/min/IP server-side. Unknown precheck results
    // (5xx / network / timeout) fall through to Firebase as before —
    // POST /api/auth/add-phone's post-OTP check stays as the race-
    // condition safety net for signup; forgot-password tolerates the
    // SMS-then-server-validation path on the rare conservative branch.
    setState((s) => ({ ...s, status: 'sending', error: null }))
    let precheckResult: 'free' | 'taken' | 'unknown' = 'unknown'
    let precheck409Message: string | null = null
    try {
      // #intl-auth-1b: the precheck now carries E.164, not bare national
      // digits. National digits alone are ambiguous across countries; the
      // actual Firebase SMS call already sends E.164, so the pre-SMS
      // availability check must match it. India-default: e164 is +91…, so
      // the server (which normalizePhone-peels +91) resolves the identical
      // 10-digit national and the result is unchanged for Indian numbers.
      await apiClient.get('/api/auth/phone-availability', {
        params: { phone: e164 },
      })
      precheckResult = 'free'
    } catch (err) {
      const apiErr = err as { response?: { status?: number; data?: { message?: string } } }
      const status = apiErr?.response?.status
      if (status === 409) {
        precheckResult = 'taken'
        precheck409Message = apiErr?.response?.data?.message ?? null
      } else {
        // 5xx / network — conservative fall-through to Firebase.
        console.log('[phone-availability] precheck non-409, falling through to Firebase', { status })
      }
    }

    if (intent === 'signup' && precheckResult === 'taken') {
      const message =
        precheck409Message ??
        'This phone number is already registered with another account. Please log in with your phone instead.'
      setState({
        status: 'duplicate',
        error: message,
        cooldownUntil: null,
        cooldownMsRemaining: null,
        idToken: null,
      })
      throw new Error(message)
    }
    if (intent === 'forgot-password' && precheckResult === 'free') {
      const message = 'No account exists with this phone number.'
      setState({
        status: 'error',
        error: message,
        cooldownUntil: null,
        cooldownMsRemaining: null,
        idToken: null,
      })
      throw new Error(message)
    }

    try {
      const verifier = getRecaptchaVerifier()
      const conf = await signInWithPhoneNumber(getFirebaseAuth(), e164, verifier)
      setConfirmation(conf)
      setState({
        status: 'awaiting-code',
        error: null,
        cooldownUntil: null,
        cooldownMsRemaining: null,
        idToken: null,
      })
    } catch (err) {
      // Canonical diagnostic — uniform with mobile + the existing
      // per-page logs that the hook is replacing.
      const fbErr = err as { code?: string; message?: string }
      console.log('[firebase-otp]', fbErr?.code, fbErr?.message, err)
      // reCAPTCHA tokens are single-use; reset so the next attempt
      // gets a fresh challenge.
      resetRecaptcha()
      const mapped = mapFirebaseAuthSendOtpError(err)
      const cooldownUntil =
        fbErr?.code === 'auth/too-many-requests' ? Date.now() + COOLDOWN_MS : null
      setState({
        status: 'error',
        error: mapped.message,
        cooldownUntil,
        // Initial remaining set inline so the consumer's "Try again in
        // M:SS" line shows the right value BEFORE the first tick fires
        // (otherwise there's a 1s gap where it reads null).
        cooldownMsRemaining: cooldownUntil !== null ? COOLDOWN_MS : null,
        idToken: null,
      })
      throw err
    }
    } finally {
      sendingRef.current = false
    }
  }, [])

  const verifyOtp = useCallback(
    async (code: string): Promise<string> => {
      if (!confirmation) {
        const message = 'No OTP sent — call sendOtp first'
        setState((s) => ({ ...s, status: 'error', error: message }))
        throw new Error(message)
      }
      setState((s) => ({ ...s, status: 'verifying', error: null }))
      try {
        const cred = await confirmation.confirm(code)
        const idToken = await cred.user.getIdToken()
        setState((s) => ({
          ...s,
          status: 'verified',
          error: null,
          idToken,
        }))
        return idToken
      } catch (err) {
        const fbErr = err as { code?: string; message?: string }
        console.log('[firebase-otp]', fbErr?.code, fbErr?.message, err)
        const mapped = mapFirebaseAuthVerifyOtpError(err)
        setState((s) => ({
          ...s,
          status: 'error',
          error: mapped.message,
        }))
        throw err
      }
    },
    [confirmation],
  )

  const reset = useCallback(() => {
    resetRecaptcha()
    setConfirmation(null)
    setState(INITIAL_STATE)
  }, [])

  return { state, sendOtp, verifyOtp, reset }
}
